import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchShareFormConfigMutation } from '@/features/config/configApi';

export const SHARE_FORM_CONFIG_DIALOG = 'shareFormConfig';

/**
 * Shares broker group configs into the user's other broker groups on their ledger: each
 * group's config for these fields is replaced with one that follows the original's edits and
 * can't be edited there until its link is broken. The originals' own groups can't be picked.
 * Opened with openDialog({ dialogName: SHARE_FORM_CONFIG_DIALOG, data: { docIds, configType,
 * originalGroupIds, skipped, sharedWith } }) (skipped: ticked ledger or default rows left
 * out). With sharedWith (from the Shared cell: one original and the [{ id, name }] it's shared
 * with now), the user's groups among them start ticked and saving makes it shared with exactly
 * the ticked ones: unticked groups lose their shared config and fall back to their ledger or
 * the default. Groups the user isn't in keep their shares. Mount it only while open, so each
 * opening starts from its data.
 */
export const ShareFormConfigDialog = () => {
	const dispatch = useDispatch();
	const { docIds, configType, originalGroupIds, skipped, sharedWith } = useSelector(
		(state) => state.dialog.data[SHARE_FORM_CONFIG_DIALOG],
	);
	const replace = Boolean(sharedWith);
	const onClose = () => dispatch(closeDialog(SHARE_FORM_CONFIG_DIALOG));
	const [batchShareFormConfig, { isLoading }] = useBatchShareFormConfigMutation();

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	// Only the user's own groups can be picked; the rest keep their shares
	const ownGroupIds = [userDetails?.brokerGroupId, ...(userDetails?.brokerGroupArray ?? [])];
	const ownSharedWithIds = (sharedWith ?? [])
		.map(({ id }) => id)
		.filter((id) => ownGroupIds.includes(id));
	const otherSharedWith = (sharedWith ?? []).filter(({ id }) => !ownGroupIds.includes(id));
	const [brokerGroupIds, setBrokerGroupIds] = useState(ownSharedWithIds);

	const removedCount = ownSharedWithIds.filter((id) => !brokerGroupIds.includes(id)).length;
	const isChanged = replace
		? brokerGroupIds.length !== ownSharedWithIds.length || removedCount > 0
		: brokerGroupIds.length > 0;

	const handleShare = async () => {
		const result = await batchShareFormConfig({ docIds, brokerGroupIds, configType, replace });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't share the config. Please try again.", error: true }),
			);
			return;
		}
		dispatch(showToast({ message: 'Shared the config', success: true }));
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`Share ${docIds.length} field${docIds.length === 1 ? '' : 's'}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Typography variant='body2'>
						{
							"This replaces the selected groups' settings for these fields with a link to yours. Shared rows follow every change you make here, and can't be edited in those groups until someone breaks the link."
						}
						{Boolean(skipped) &&
							` ${skipped} ticked ledger or default row${skipped === 1 ? " isn't" : "s aren't"} shared; copy ${skipped === 1 ? 'it' : 'them'} instead.`}
						{Boolean(removedCount) &&
							` ${removedCount} unticked group${removedCount === 1 ? ' loses its' : 's lose their'} shared row and will follow the ledger or default again.`}
						{Boolean(otherSharedWith.length) &&
							` Also shared with ${otherSharedWith.map(({ name }) => name).join(', ')}, which you can't change.`}
					</Typography>
					<ShareField label='Broker groups'>
						<BrokerGroupSelect
							brokerGroupId={brokerGroupIds}
							onChange={setBrokerGroupIds}
							multiple
							isGroupDisabled={(group) =>
								group.ledger !== userDetails?.broker?.ledger ||
								originalGroupIds.includes(group.id)
							}
						/>
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleShare}
					disabled={!isChanged || isLoading}
				>
					Share
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ShareFormConfigDialog.displayName = 'ShareFormConfigDialog';
